import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { EventsService } from "@/api/events";
import { useToast } from "@/hooks/use-toast";
import { getCurrentClientData } from "@/services/authServices";

interface RecipientData {
  email: string;
  id: string;
  full_name: string;
}

interface EmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allAttendees?: RecipientData[];
  defaultRecipient?: RecipientData[];
  eventId: string;
}

export function EmailModal({
  open,
  onOpenChange,
  allAttendees,
  defaultRecipient,
  eventId,
}: EmailModalProps) {
  const { toast } = useToast();
  const { listDashboardAttendees, sendEmailApi } = EventsService;
  const loggedInUser = getCurrentClientData();

  const [attendees, setAttendees] = useState<RecipientData[]>([]);
  const [recipients, setRecipients] = useState<RecipientData[]>([]);
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboardAttendees();
  }, []);

  useEffect(() => {
    if (defaultRecipient) setRecipients(defaultRecipient);
  }, [defaultRecipient]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const loadDashboardAttendees = async () => {
    try {
      const res: any = await listDashboardAttendees(
        eventId,
        loggedInUser?.token
      );

      if (res.statusCode === 200 || res.statusCode === 201) {
        setAttendees(
          res.body.attendees.map((attendee) => ({
            id: attendee.attendee_id,
            email: attendee.email,
            full_name: attendee.full_name,
          }))
        );
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error fetching your attendees.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error fetching your attendees.",
        variant: "destructive",
      });
      return error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (recipients.length === 0) {
      toast({
        title: "No recipients selected",
        description: "Please select at least one recipient.",
        variant: "destructive",
      });
      return;
    }
    if (!formData.subject || !formData.message) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields before sending.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    const dt = {
      event_id: eventId,
      attendee_ids: recipients.map((item) => item.id),
      // subject: formData.subject,
      email_body: formData.message.trim(),
    };
    try {
      const res: any = await sendEmailApi(dt, loggedInUser.token);
      if (res.statusCode === 200 || res.statusCode === 201) {
        toast({
          title: "Successful",
          description:
            res?.body?.message ||
            `Email sent to ${recipients.length} attendees.`,
        });

        onOpenChange(false);
        setFormData((prev) => ({ ...prev, subject: "", message: "" }));
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-md p-6 focus:outline-none">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-xl font-semibold">
              Send Email
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-gray-500 hover:text-gray-700">X</button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit}>
            {/* <div className="mb-4">
              <label
                htmlFor="recipient"
                className="block text-sm font-medium mb-1"
              >
                Recipient
              </label>
              <input
                type="email"
                id="recipient"
                name="recipient"
                disabled={true}
                value={defaultRecipient.map((item) => item.email).join(", ")}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div> */}
            <MultiSelectDropdown
              recipients={allAttendees ? allAttendees : attendees}
              selected={recipients}
              onSelect={setRecipients}
              placeholder="Select recipients..."
            />
            <div className="mb-4">
              <label
                htmlFor="subject"
                className="block text-sm font-medium mb-1"
              >
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="message"
                className="block text-sm font-medium mb-1"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={5}
                required
              />
            </div>

            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
              >
                {loading ? (
                  <span className="animate-spin">Sending...</span>
                ) : (
                  <span>Send Email</span>
                )}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
